import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { epApi, showError } from '../api';
import { mdy, RECOMMENDER_STATUS } from '../format';
import { TaskView } from '../types';

interface Props {
  task: TaskView;
  studentId: number;
  programName: string;
  onClose: () => void;
  onSaved: (t: TaskView) => void;
}

const isLetter = (t: TaskView) => /^rec_/.test(t.requirement_key ?? '') || t.recommender_status != null;
const isEssay = (t: TaskView) => /^essay/.test(t.requirement_key ?? '') || t.essay_prompt != null;
const fromLabel = (t: TaskView) => (t.created_by_role === 'counselor' ? 'From your counselor' : '');

/** Open a task: recommender, essay prompt and draft, your date, notes. Never a password. */
export default function TaskModal({ task, studentId, programName, onClose, onSaved }: Props) {
  const [f, setF] = useState({
    title: task.title,
    recommender_name: task.recommender_name ?? '',
    recommender_role: task.recommender_role ?? '',
    recommender_email: task.recommender_email ?? '',
    recommender_status: task.recommender_status ?? 'not_asked',
    essay_prompt: task.essay_prompt ?? '',
    word_limit: task.word_limit != null ? String(task.word_limit) : '',
    draft_link: task.draft_link ?? '',
    student_target_date: task.student_target_date ?? '',
    notes: task.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const ro = !task.can_edit;

  useEffect(() => {
    first.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const save = async (extra: Partial<TaskView> = {}) => {
    const draft = f.draft_link.trim() && !/^https?:\/\//i.test(f.draft_link.trim()) ? `https://${f.draft_link.trim()}` : f.draft_link.trim();
    if (draft && /\s/.test(draft)) {
      toast.error('The draft link should be a web address, like docs.google.com/...');
      return;
    }
    setBusy(true);
    const body: Partial<TaskView> = {
      student_target_date: f.student_target_date || null,
      notes: f.notes.trim() || null,
      ...extra,
    };
    if (task.origin === 'student_added' && f.title.trim()) body.title = f.title.trim();
    if (isLetter(task)) {
      Object.assign(body, {
        recommender_name: f.recommender_name.trim() || null,
        recommender_role: f.recommender_role.trim() || null,
        recommender_email: f.recommender_email.trim() || null,
        recommender_status: f.recommender_status,
      });
    }
    if (isEssay(task)) {
      Object.assign(body, {
        essay_prompt: f.essay_prompt.trim() || null,
        word_limit: f.word_limit ? Number(f.word_limit) : null,
        draft_link: draft || null,
      });
    }
    try {
      const saved = await epApi.updateTask(studentId, task.id, body);
      onSaved(saved);
      toast.success(extra.status === 'done' ? 'Marked done' : f.student_target_date !== (task.student_target_date ?? '') ? `Moved to ${mdy(f.student_target_date)}. Your calendar updates too.` : 'Saved');
      onClose();
    } catch (e) {
      showError(e, 'Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-task-title" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        {task.origin === 'student_added' && !ro ? (
          <input ref={first} className="in" aria-label="Task name" value={f.title} onChange={set('title')} style={{ fontSize: 17, fontWeight: 400 }} />
        ) : (
          <h2 id="ep-task-title">{task.title}</h2>
        )}
        <p className="muted small" style={{ marginTop: 4, fontWeight: 500 }}>
          {[programName, fromLabel(task)].filter(Boolean).join(' · ')}
        </p>

        {isLetter(task) && (
          <div className="stack" style={{ marginTop: 16 }}>
            <div className="grid2">
              <div>
                <label className="lbl" htmlFor="ep-rn">Recommender</label>
                <input id="ep-rn" ref={task.origin === 'student_added' ? undefined : first} className="in" placeholder="Name" value={f.recommender_name} onChange={set('recommender_name')} disabled={ro} maxLength={120} />
              </div>
              <div>
                <label className="lbl" htmlFor="ep-rr">Role</label>
                <input id="ep-rr" className="in" placeholder="Biology teacher" value={f.recommender_role} onChange={set('recommender_role')} disabled={ro} maxLength={80} />
              </div>
              <div>
                <label className="lbl" htmlFor="ep-re">Email</label>
                <input id="ep-re" className="in" type="email" placeholder="name@school.org" value={f.recommender_email} onChange={set('recommender_email')} disabled={ro} maxLength={200} />
              </div>
              <div>
                <label className="lbl" htmlFor="ep-rs">Where it stands</label>
                <select id="ep-rs" className="in" value={f.recommender_status} onChange={set('recommender_status')} disabled={ro}>
                  {RECOMMENDER_STATUS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="muted small">When the letter is sent, this task is marked done.</p>
          </div>
        )}

        {isEssay(task) && (
          <div className="stack" style={{ marginTop: 16 }}>
            <div>
              <label className="lbl" htmlFor="ep-ep">Essay prompt</label>
              <textarea id="ep-ep" className="in" placeholder="Paste the prompt from the application" value={f.essay_prompt} onChange={set('essay_prompt')} disabled={ro} />
            </div>
            <div className="grid2">
              <div>
                <label className="lbl" htmlFor="ep-wl">Word limit</label>
                <input id="ep-wl" className="in" inputMode="numeric" value={f.word_limit} onChange={(e) => setF({ ...f, word_limit: e.target.value.replace(/\D/g, '').slice(0, 5) })} disabled={ro} />
              </div>
              <div>
                <label className="lbl" htmlFor="ep-dl">Your draft</label>
                <input id="ep-dl" className="in" placeholder="Link to your Google Doc" value={f.draft_link} onChange={set('draft_link')} disabled={ro} />
              </div>
            </div>
          </div>
        )}

        <div className="grid2" style={{ marginTop: 16 }}>
          <div>
            <label className="lbl" htmlFor="ep-yd">Your date</label>
            <input id="ep-yd" className="in" type="date" value={f.student_target_date} onChange={set('student_target_date')} disabled={ro} />
          </div>
          <div>
            <span className="lbl">Program deadline</span>
            <p style={{ height: 42, display: 'flex', alignItems: 'center', fontWeight: 500 }}>{task.official_deadline ? mdy(task.official_deadline) : 'Not posted yet'}</p>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="lbl" htmlFor="ep-nt">Notes</label>
          <textarea id="ep-nt" className="in" placeholder="Anything to remember" value={f.notes} onChange={set('notes')} disabled={ro} maxLength={2000} />
        </div>

        {task.link && (
          <p className="small" style={{ marginTop: 10 }}>
            <a href={task.link} target="_blank" rel="noopener noreferrer">Open the program page</a>
          </p>
        )}

        <div className="foot">
          <div className="row">
            {!ro && (
              <button type="button" className="btn primary" disabled={busy} onClick={() => save()}>
                Save
              </button>
            )}
            {!ro && task.status !== 'done' && (
              <button type="button" className="btn" disabled={busy} onClick={() => save({ status: 'done' })}>
                Mark done
              </button>
            )}
            {!ro && task.status === 'done' && (
              <button type="button" className="btn" disabled={busy} onClick={() => save({ status: 'not_started' })}>
                Not done yet
              </button>
            )}
          </div>
          <button type="button" className="btn" onClick={onClose}>
            {ro ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
